/**
 * Card primitive.
 *
 * Token-backed content container. No Radix dependency (Card is a
 * presentational shell with no interaction semantics — a plain `<div>`
 * is the right element). No variants in Cluster 1; when Cluster 2's
 * curated-content cards land a split (elevated vs. flat) can be
 * introduced the same way Button exposes its variant lookup. Keeping
 * Card minimal today avoids shipping unused surface.
 *
 * The container exposes `rounded-xl` (softer than Button's `rounded-lg`
 * so nested primitives — a Button inside a Card — read cleanly and
 * cards paint with the prominent corner treatment the prototype
 * favours), a one-pixel `border-border`, the `bg-card` token
 * (`--color-card` — the slightly-elevated light-scheme surface), and
 * token-scaled internal padding. Every class is a token-backed
 * Tailwind utility (FR-002, FR-047). Refs are forwarded as a plain
 * prop per React 19 conventions; callers that need to attach a
 * `useRef<HTMLDivElement>` for measurement or scroll positioning can
 * do so without a wrapper.
 */

import type { HTMLAttributes, Ref } from 'react';

import { cn } from './utils.js';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
}

const baseClasses = 'bg-card text-card-foreground border border-border rounded-xl p-4';

export function Card({ className, ref, ...rest }: CardProps) {
  return <div ref={ref} className={cn(baseClasses, className)} {...rest} />;
}
